import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { supabase } from "@/integrations/supabase/client"
import { toast } from "sonner"
import { Database, OrderStatus } from "@/types/database.types"
import { useQueryClient } from '@tanstack/react-query'

interface OrderStatusActionsProps {
  orderId: string
  currentStatus: OrderStatus | string
  stripePaymentIntentId?: string | null
  // storeId: string; // Keep if used by updateGenericOrderStatus or other actions
}

type Tables = Database['public']['Tables']
type Order = Tables['orders']['Row']
// type OrderUpdate = Partial<Tables['orders']['Update']>
// type DirectOrderUpdate = Tables['orders']['Update']; // Not used, can be removed if not needed elsewhere

export function OrderStatusActions({ orderId, currentStatus, stripePaymentIntentId }: OrderStatusActionsProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [orderDetails, setOrderDetails] = useState<any>(null)
  const queryClient = useQueryClient()

  // Fetch order details to get delivery_type
  useEffect(() => {
    const fetchOrderDetails = async () => {
      const { data, error } = await supabase
        .from('orders')
        .select('delivery_type')
        .eq('id', orderId as any)
        .single()
      
      if (!error && data) {
        setOrderDetails(data)
      }
    }
    fetchOrderDetails()
  }, [orderId])

  const isPickupOrder = orderDetails?.delivery_type === 'pickup'

  // Existing function to update status for non-payment related changes
  const updateGenericOrderStatus = async (newStatus: OrderStatus) => {
    try {
      setIsLoading(true)
      
      // Call our Edge Function to update status & send notifications server-side
      // This likely needs to be reviewed if 'update-order-status' sends its own emails
      // or if it should just update the status.
      const { data, error } = await supabase.functions.invoke('update-order-status', {
        body: JSON.stringify({ orderId, newStatus }),
      })
      if (error || !data?.success) {
        const msg = error?.message || data?.message || 'Failed to update status'
        console.error('Error updating generic order status:', msg)
        throw new Error(msg)
      }
      
      toast.success(`Order marked as ${newStatus.replace(/_/g, ' ').toLowerCase()}`)
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['order', orderId] });
    } catch (error) {
      console.error('Error updating generic order status:', error)
      toast.error('Failed to update generic order status')
    } finally {
      setIsLoading(false)
    }
  }

  const handleAcceptAndCapture = async () => {
    if (!stripePaymentIntentId) {
      toast.error("Stripe Payment Intent ID is missing. Cannot capture payment.");
      return;
    }
    if (!confirm("Are you sure you want to accept this order and capture payment? This action cannot be undone.")) {
      return;
    }

    setIsLoading(true);
    toast.info("Attempting to capture Stripe payment...");

    try {
      const { data: captureResponse, error: captureError } = await supabase.functions.invoke(
        'capture-stripe-payment', // Ensure this is calling the STRIPE function
        {
          body: {
            order_id: orderId,
            payment_intent_id: stripePaymentIntentId,
          },
        }
      );

      if (captureError) {
        console.error('Error invoking capture-stripe-payment:', captureError);
        toast.error(`Capture failed: ${captureError.message || 'Function invocation error.'}`);
        throw captureError; 
      }

      if (!captureResponse?.success || captureResponse.error) {
        console.error('Capture payment function returned error:', captureResponse?.error);
        toast.error(`Capture failed: ${captureResponse?.error || 'Unknown error from capture function.'}`);
        return; 
      }
      
      toast.success('Payment captured and order confirmed! Status updated.');
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['order', orderId] });
      // The Edge function should have updated status to 'Confirmed' or 'Processing'

    } catch (error: any) {
      // Avoid double toasting if already handled by specific error checks above
      if (!(error.message?.includes('Capture failed'))) {
        toast.error('An unexpected error occurred while capturing payment.');
      }
      console.error('Full error in handleAcceptAndCapture:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRejectOrder = async () => {
    if (!stripePaymentIntentId) {
      toast.error("Stripe Payment Intent ID is missing. Cannot reject/cancel payment.");
      return;
    }
    if (!confirm("Are you sure you want to reject this order? This will attempt to cancel the payment authorization on Stripe and mark the order as cancelled.")) {
      return;
    }

    setIsLoading(true);
    toast.info("Attempting to reject order and cancel payment authorization...");
    try {
      console.log(`[OrderStatusActions] Attempting to reject order ${orderId} and cancel Stripe PI: ${stripePaymentIntentId}`);
      
      const { data: cancelResponse, error: cancelError } = await supabase.functions.invoke('cancel-stripe-payment-intent', {
        body: { 
          order_id: orderId,
          payment_intent_id: stripePaymentIntentId 
        },
      });

      if (cancelError) {
        console.error('Error invoking cancel-stripe-payment-intent:', cancelError);
        toast.error(`Rejection failed: ${cancelError.message || 'Function invocation error.'}`);
        throw cancelError;
      }

      if (!cancelResponse?.success || cancelResponse.error) {
        console.error('Cancel payment function returned error:', cancelResponse?.error);
        toast.error(`Rejection failed: ${cancelResponse?.error || 'Unknown error from cancel function.'}`);
        return; 
      }

      toast.success('Order rejected successfully. Payment authorization has been cancelled.');
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['order', orderId] });
      // The Edge function should have updated order status to 'cancelled' and payment_status to 'voided' (or similar)

    } catch (error: any) {
      if (!error.message?.includes('Reject failed') && !error.message?.includes('Rejection failed')) { 
        toast.error('An unexpected error occurred while rejecting the order.');
      }
      console.error('Full error in handleRejectOrder:', error);
    } finally {
      setIsLoading(false);
    }
  };


  // No actions if order is delivered, cancelled, etc.
  if (['delivered', 'cancelled', 'refund_requested', 'picked_up'].includes(currentStatus as string)) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-2"> {/* Added flex-wrap for better responsiveness */}
      {/* Actions for orders awaiting florist confirmation (payment authorized) */}
      {currentStatus === 'awaiting_florist_confirmation' && (
        <>
          <Button
            variant="outline"
            onClick={handleAcceptAndCapture}
            disabled={isLoading || !stripePaymentIntentId} // Also disable if no PI ID
            className="border-blue-500 text-blue-500 hover:bg-blue-50 hover:text-blue-600"
            title={!stripePaymentIntentId ? "Stripe Payment ID missing" : "Accept order and capture payment"}
          >
            Accept & Capture Payment
          </Button>
          <Button
            variant="outline"
            onClick={handleRejectOrder} 
            disabled={isLoading || !stripePaymentIntentId} // Also disable if no PI ID
            className="border-red-500 text-red-500 hover:bg-red-50 hover:text-red-600"
            title={!stripePaymentIntentId ? "Stripe Payment ID missing" : "Reject order and attempt to cancel payment authorization"}
          >
            Reject Order
          </Button>
        </>
      )}

      {/* Actions for original 'pending' state - this state should ideally not require payment actions by florist */}
      {currentStatus === 'pending' && (
         <p className="text-sm text-gray-500">Order is pending initial payment authorization.</p>
      )}
      
      {/* Actions for orders confirmed by florist and payment captured (now 'processing' or 'confirmed') */}
      {/* Ensure your OrderStatus enum and workflow align with these status strings */}
      {(currentStatus === 'confirmed' || currentStatus === 'processing') && (
        <Button
          variant="outline"
          onClick={() => updateGenericOrderStatus('preparing')}
          disabled={isLoading}
          className="border-green-500 text-green-500 hover:bg-green-50 hover:text-green-600"
        >
          Mark as Preparing
        </Button>
      )}

      {currentStatus === 'preparing' && (
        <Button
          variant="outline"
          onClick={() => updateGenericOrderStatus(isPickupOrder ? 'ready_for_pickup' as OrderStatus : 'out_for_delivery')}
          disabled={isLoading}
          className="border-green-500 text-green-500 hover:bg-green-50 hover:text-green-600"
        >
          {isPickupOrder ? 'Mark Ready for Pickup' : 'Mark Out for Delivery'}
        </Button>
      )}

      {currentStatus === 'ready_for_pickup' && (
        <Button
          variant="outline"
          onClick={() => updateGenericOrderStatus('picked_up' as OrderStatus)}
          disabled={isLoading}
          className="border-green-500 text-green-500 hover:bg-green-50 hover:text-green-600"
        >
          Mark as Picked Up
        </Button>
      )}

      {currentStatus === 'out_for_delivery' && (
        <Button
          variant="outline"
          onClick={() => updateGenericOrderStatus('delivered')}
          disabled={isLoading}
          className="border-green-500 text-green-500 hover:bg-green-50 hover:text-green-600"
        >
          Mark as Delivered
        </Button>
      )}
    </div>
  )
}